import React, {useState} from 'react';
import {Box, Center, HStack, VStack, Wrap, WrapItem, useTheme} from '@chakra-ui/react';
import {TextG12, TextG14, TextG16} from '../../../components/typography';
import useAuth from '../../../hooks/useAuth';
import UserAvatar from '../../../components/UserAvatar';
import {useGetUserAddresses} from '../../../hooks/useUserAddress';
import {useConversationStarters} from '../../../hooks/useConversationStarters';
import {useAvailability} from '../../../hooks/useAvailability';
import {useCityProfile} from '../../../hooks/useCityProfile';
import {DAY_ABBREV} from '../../../utils/availability';
import {CITY_DURATION_LABELS, DURATION_LABELS} from '../../../utils/cityDurationLabels';
import {getDisplayName} from '../../../utils/nameFormatters';
import ProfileIdentity from '../../../components/Profile/ProfileIdentity';

const GENERAL_LABELS: Record<string, {label: string; emoji: string}> = {
  Morning: {label: 'Mornings', emoji: '🌅'},
  Afternoon: {label: 'Afternoons', emoji: '✨'},
  Evening: {label: 'Evenings', emoji: '🏙️'},
};

const TIME_EMOJI: Record<string, string> = {
  Morning: '🌤️',
  Afternoon: '✨',
  Evening: '🏙️',
};

const INITIAL_VISIBLE_STARTERS = 3;
const INITIAL_VISIBLE_AVAILABILITY = 4;

const ProfilePreview = () => {
  const theme = useTheme();
  const {user} = useAuth();
  const {userAddresses} = useGetUserAddresses({
    userId: user?.id,
    addressType: 'HOME',
  });
  const [showAllStarters, setShowAllStarters] = useState(false);
  const [showAllAvailability, setShowAllAvailability] = useState(false);

  const {data: conversationStarters = []} = useConversationStarters();
  const {data: availability} = useAvailability();
  const {data: cityProfile} = useCityProfile();

  // ProfilePreview shows what other users see, so display first name only
  // (matches NeighborProfile and the rest of the user-facing app).
  const displayName = getDisplayName(user);
  const homeAddress = userAddresses?.[0];
  const rawNeighborhood = homeAddress?.neighborhood || homeAddress?.city;
  const neighborhood =
    typeof rawNeighborhood === 'object' && rawNeighborhood !== null
      ? (rawNeighborhood as any).name
      : rawNeighborhood;

  const answeredStarters = conversationStarters
    .filter((s) => s.answer && s.answer.trim().length > 0)
    .map((s) => ({
      prompt: s.prompt?.text || '',
      answer: s.answer,
    }));

  // Build flat availability chip list
  const availabilityChips: {label: string; emoji: string}[] = [];
  (availability?.general || []).forEach((id) => {
    const item = GENERAL_LABELS[id];
    if (item) availabilityChips.push(item);
  });
  Object.entries(availability?.specific || {}).forEach(([day, times]) => {
    const abbrev = DAY_ABBREV[day] || day;
    times.forEach((t) => {
      const emoji = TIME_EMOJI[t] || '✨';
      const label = `${abbrev} ${t}`;
      availabilityChips.push({label, emoji});
    });
  });

  const hasAvailability = availabilityChips.length > 0;
  const hasCityDuration = !!cityProfile?.city_duration;
  const hasNeighborhoodDuration = !!cityProfile?.neighborhood_duration;

  const visibleStarters = showAllStarters
    ? answeredStarters
    : answeredStarters.slice(0, INITIAL_VISIBLE_STARTERS);
  const hiddenStartersCount = answeredStarters.length - INITIAL_VISIBLE_STARTERS;

  const visibleAvailability = showAllAvailability
    ? availabilityChips
    : availabilityChips.slice(0, INITIAL_VISIBLE_AVAILABILITY);
  const hiddenAvailabilityCount = availabilityChips.length - INITIAL_VISIBLE_AVAILABILITY;

  const isEmpty =
    !user?.aboutMe &&
    answeredStarters.length === 0 &&
    !hasAvailability &&
    !hasCityDuration &&
    !hasNeighborhoodDuration;

  return (
    <Box pb={8}>
      {/* Avatar + Name + Badges */}
      <ProfileIdentity
        name={displayName}
        handle={user?.handle}
        neighborhood={neighborhood}
        city={neighborhood ? 'San Diego' : undefined}
        hasActiveMembership={user?.hasActiveMembership}
        avatarSlot={<UserAvatar user={user} w="100px" h="100px" fontSize="30px" p={0} />}
      />

      <Box px={4} pt={4}>
      {/* Introduction — prompt in regular text, answer in bold */}
      {user?.aboutMe && (
        <Box mb={6}>
          <TextG16 mt="0" color="#4D4D4D" lineHeight="20px" mb={2}>
            I could happily nerd out about...
          </TextG16>
          <TextG16 mt="0" fontWeight="700" lineHeight="22px" fontSize="15px">
            {user.aboutMe}
          </TextG16>
        </Box>
      )}

      {/* Conversation Starters — cards */}
      {answeredStarters.length > 0 && (
        <Box mb={6}>
          <VStack spacing={3} align="stretch">
            {visibleStarters.map(({prompt, answer}, i) => (
              <Box
                key={i}
                border="1px solid #E5E5E5"
                borderRadius="12px"
                p={4}
                boxShadow="0 0 16px 0 rgba(0, 0, 0, 0.08)">
                <TextG14 mt="0" color="#555" lineHeight="18px" mb={1}>
                  {prompt}
                </TextG14>
                <TextG14 mt="0" fontWeight="700" lineHeight="22px">
                  {answer}
                </TextG14>
              </Box>
            ))}
          </VStack>
          {!showAllStarters && hiddenStartersCount > 0 && (
            <TextG14
              mt="12px"
              color="#1A1A1A"
              fontWeight="600"
              cursor="pointer"
              onClick={() => setShowAllStarters(true)}>
              +{hiddenStartersCount} more conversation starter{hiddenStartersCount > 1 ? 's' : ''}
            </TextG14>
          )}
        </Box>
      )}

      {/* Availability */}
      {hasAvailability && (
        <Box mb={6}>
          <TextG14 mt="0" fontWeight="700" mb={3}>
            I'm free to connect on...
          </TextG14>
          <Wrap spacing={2}>
            {visibleAvailability.map((chip, i) => (
              <WrapItem key={i}>
                <HStack
                  px={3}
                  py={1.5}
                  borderRadius="20px"
                  border="1px solid #E5E5E5"
                  bg="white"
                  spacing={1.5}
                  boxShadow="0 0 16px 0 rgba(0, 0, 0, 0.08)">
                  <span style={{fontSize: '14px'}}>{chip.emoji}</span>
                  <TextG14 mt="0">{chip.label}</TextG14>
                </HStack>
              </WrapItem>
            ))}
          </Wrap>
          {!showAllAvailability && hiddenAvailabilityCount > 0 && (
            <TextG14
              mt="12px"
              color="#1A1A1A"
              fontWeight="600"
              cursor="pointer"
              onClick={() => setShowAllAvailability(true)}>
              +{hiddenAvailabilityCount} more availability window
              {hiddenAvailabilityCount > 1 ? 's' : ''}
            </TextG14>
          )}
        </Box>
      )}

      {/* My time in San Diego */}
      {hasCityDuration && (
        <Box mb={6}>
          <TextG14 mt="0" fontWeight="700" mb={3}>
            My time in San Diego...
          </TextG14>
          <HStack
            px={3}
            py={1.5}
            borderRadius="20px"
            border="1px solid #E5E5E5"
            bg="white"
            spacing={1.5}
            display="inline-flex"
            boxShadow="0 0 16px 0 rgba(0, 0, 0, 0.08)">
            <span style={{fontSize: '14px'}}>
              {CITY_DURATION_LABELS[cityProfile!.city_duration!]?.emoji || '🌴'}
            </span>
            <TextG14 mt="0">
              {CITY_DURATION_LABELS[cityProfile!.city_duration!]?.label || cityProfile!.city_duration}
            </TextG14>
          </HStack>
        </Box>
      )}

      {/* Neighborhood duration */}
      {hasNeighborhoodDuration && neighborhood && (
        <Box mb={6}>
          <TextG14 mt="0" fontWeight="700" mb={3}>
            I've been in {neighborhood} neighborhood...
          </TextG14>
          <HStack
            px={3}
            py={1.5}
            borderRadius="20px"
            border="1px solid #E5E5E5"
            bg="white"
            spacing={1.5}
            display="inline-flex"
            boxShadow="0 0 16px 0 rgba(0, 0, 0, 0.08)">
            <TextG14 mt="0">
              {DURATION_LABELS[cityProfile!.neighborhood_duration!] ||
                cityProfile!.neighborhood_duration}
            </TextG14>
          </HStack>
        </Box>
      )}

      {/* Empty state */}
      {isEmpty && (
        <Center py={12}>
          <TextG14 mt="0" color={theme.colors.grey} textAlign="center">
            Complete your profile to show others what you're about!
          </TextG14>
        </Center>
      )}
      </Box>
    </Box>
  );
};

export default ProfilePreview;
